import React, { useState, useEffect, useRef } from "react";
import Plot from "react-plotly.js";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "../../assets/styles/exec/NewChatInterface.scss";
// import { Tooltip as ReactTooltip } from "react-tooltip";

// Import MUI elements
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";

function NewChatInterface({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  const [showAddTrainingDataModal, setShowAddTrainingDataModal] =
    useState<boolean>(false);
  const [trainingDataStatus, setTrainingDataStatus] = useState<string>("");
  const [currentTrainingData, setCurrentTrainingData] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newQuestion, setNewQuestion] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [questionHistory, setQuestionHistory] = useState<any>([]);
  const [newTrainingData, setNewTrainingData] = useState<{
    training_data_type: string;
    question: string;
    content: string;
  }>({
    training_data_type: "question",
    question: "",
    content: "",
  });
  const [trainingData, setTrainingData] = useState<any[]>([]);
  const [selectedTrainingExample, setSelectedTrainingExample] =
    useState<any>(null);
  // const [audioFile, setAudioFile] = useState<File | null>(null);

  useEffect(() => {
    getQuestionHistory();
    getTrainingData();
  }, []);

  const sendQuestion = async (question: string) => {
    setLoading(true);
    const userTimestamp = new Date().getTime();
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: "user", text: question, timestamp: userTimestamp },
    ]);
    setNewQuestion("");

    try {
      const response = await fetch(
        `/api/v0/generate_sql?question=${encodeURIComponent(question)}`
      );
      const data = await response.json();
      const id = data.id;
      const generatedSQL = data.text;

      const sqlTimestamp = new Date().getTime();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "",
          sql: generatedSQL,
          id,
          timestamp: sqlTimestamp,
        },
      ]);

      const sqlResponse = await fetch(`/api/v0/run_sql?id=${id}`);
      const sqlData = await sqlResponse.json();
      const parsedDF = JSON.parse(sqlData.df.replace(/\bNaN\b/g, '"NaN"'));

      const dfTimestamp = new Date().getTime();
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: "", df: parsedDF, id, timestamp: dfTimestamp },
      ]);

      const figureResponse = await fetch(
        `/api/v0/generate_plotly_figure?id=${id}`
      );
      const figureData = await figureResponse.json();

      const figureTimestamp = new Date().getTime();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "",
          fig: figureData.fig,
          id,
          timestamp: figureTimestamp,
        },
      ]);

      const summaryResponse = await fetch(`/api/v0/generate_summary?id=${id}`);
      const summaryData = await summaryResponse.json();

      const summaryTimestamp = new Date().getTime();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: summaryData.text,
          id,
          timestamp: summaryTimestamp,
        },
      ]);

      setCurrentTrainingData({
        question: question,
        sql: generatedSQL,
      });
      //   setShowAddTrainingDataModal(true);

      getQuestionHistory();

      //   const addTrainingDataConfirmation = window.confirm('Do you want to add this query as training data?');

      getQuestionHistory();
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", error: error },
      ]);
    }

    setLoading(false);
  };

  const handleAddTrainingData = async () => {
    if (currentTrainingData) {
      await fetch("/api/v0/train", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(currentTrainingData),
      });

      getTrainingData();
      //   setShowAddTrainingDataModal(false);
    }
  };

  const downloadCSV = async (id: string) => {
    const response = await fetch(`/api/v0/download_csv?id=${id}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "data.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getQuestionHistory = async () => {
    const response = await fetch("/api/v0/get_question_history");
    const data = await response.json();
    setQuestionHistory(data.questions);
  };

  const loadQuestion = async (id: string) => {
    setMessages([]);
    setLoading(true);

    const response = await fetch(`/api/v0/load_question?id=${id}`);
    const data = await response.json();

    setMessages([
      { sender: "user", text: data.question },
      { sender: "bot", text: "SQL:", sql: data.sql, id },
      { sender: "bot", text: "Results:", df: data.df, id },
      {
        sender: "bot",
        text: "Follow-up questions:",
        followup_questions: data.followup_questions,
        id,
      },
    ]);

    setLoading(false);
  };

  const getTrainingData = async () => {
    const response = await fetch("/api/v0/get_training_data");
    const data = await response.json();
    setTrainingData(JSON.parse(data.df));
  };

  const addTrainingData = async () => {
    try {
      const trainingData = {
        question: newTrainingData.question,
        sql:
          newTrainingData.training_data_type === "sql"
            ? newTrainingData.content
            : undefined,
        ddl:
          newTrainingData.training_data_type === "ddl"
            ? newTrainingData.content
            : undefined,
        documentation:
          newTrainingData.training_data_type === "documentation"
            ? newTrainingData.content
            : undefined,
      };

      const response = await fetch("/api/v0/train", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(trainingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error);
      }

      setNewTrainingData({
        training_data_type: "question",
        question: "",
        content: "",
      });
      getTrainingData();
    } catch (error) {
      alert("Error adding training data: " + error);
    }
  };

  const removeTrainingData = async (id: string) => {
    if (window.confirm("Are you sure you want to remove this training data?")) {
      try {
        await fetch("/api/v0/remove_training_data", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        });
        getTrainingData();
      } catch (error) {
        alert("Error removing training data: " + error);
      }
    }
  };

  const messagesContainerRef = useRef(null);
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      (messagesContainerRef.current as HTMLElement).scrollTop = (
        messagesContainerRef.current as HTMLElement
      ).scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
    setTrainingDataStatus("");
  }, [messages]);

  const showTrainingExampleContent = (example: any) => {
    setSelectedTrainingExample(example);
  };

  const handleReset = () => {
    setMessages([]);
    setNewQuestion("");
    setLoading(false);
    setQuestionHistory([]);
    setNewTrainingData({
      training_data_type: "question",
      question: "",
      content: "",
    });
  };

  // const startRecording = async () => {
  //   try {
  //     const response = await fetch('/api/start_recording', {
  //       method: 'POST',
  //     });
  //     const data = await response.json();
  //     console.log('Recording started');
  //     sendQuestion(data.text);
  //   } catch (error) {
  //     console.error('Error starting recording:', error);
  //   }
  // };

  // const onAudioFileChange = (event) => {
  //   setAudioFile(event.target.files[0]);
  // };

  // const sendAudio = async () => {
  //   if (audioFile) {
  //     const formData = new FormData();
  //     formData.append('file', audioFile);

  //     try {
  //       const response = await fetch('/api/speech_to_text', {
  //         method: 'POST',
  //         body: formData,
  //       });
  //       const data = await response.json();
  //       sendQuestion(data.text);
  //     } catch (error) {
  //       console.error('Error converting speech to text:', error);
  //     }
  //   }
  // };

  return (
    <div className={`App ${mode ? "dark" : ""} new-chat-container`}>
      <div className='container mt-4'>
        <div className='row'>
          <div className='col-md-8'>
            <div
              className='card chat-container mb-4'
              ref={messagesContainerRef}
            >
              <div className='card-body'>
                {messages.length === 0 ? (
                  <div className='empty-state'>
                    <i className='fas fa-comment-dots'></i>
                    <p>Ask the call center AI data agent a question</p>
                  </div>
                ) : (
                  <div className='messages-container'>
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`message ${message.sender} ${
                          message.fig && "chat-chart-container"
                        }`}
                      >
                        <div className='message-text'>
                          {message.text && (
                            <div className='collapse-container'>
                              <div className='collapse' id={`summary-${index}`}>
                                <p>{message.text}</p>
                              </div>
                            </div>
                          )}
                          {message.fig && (
                            <div className='collapse-container'>
                              <div className='collapse' id={`figure-${index}`}>
                                <Plot
                                  data={JSON.parse(message.fig).data}
                                  layout={JSON.parse(message.fig).layout}
                                />
                              </div>
                              <div className='add-train-buttons'>
                                <p>Add training data</p>
                                <Button
                                  className='download-btn add-button'
                                  variant='contained'
                                  endIcon={<AddIcon />}
                                  onClick={() => {
                                    handleAddTrainingData();
                                    setTrainingDataStatus(
                                      "Training data added"
                                    );
                                  }}
                                >
                                  Yes
                                </Button>
                                <Button
                                  className='download-btn'
                                  variant='contained'
                                  endIcon={<RemoveIcon />}
                                  onClick={() => {
                                    setTrainingDataStatus(
                                      "Training data not added"
                                    );
                                  }}
                                >
                                  No
                                </Button>
                              </div>
                              {trainingDataStatus && (
                                <p>
                                  <CheckCircleIcon />
                                  {trainingDataStatus}
                                </p>
                              )}
                            </div>
                          )}
                          {message.df && (
                            <div className='collapse-container'>
                              <div className='collapse' id={`table-${index}`}>
                                <table className='table table-striped'>
                                  <thead>
                                    <tr>
                                      {Object.keys(message.df[0]).map(
                                        (column) => (
                                          <th key={column}>{column}</th>
                                        )
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {message.df.map(
                                      (row: any, rowIndex: number) => (
                                        <tr key={rowIndex}>
                                          {Object.keys(row).map((column) => (
                                            <td key={column}>
                                              {typeof row[column] ===
                                                "number" ||
                                              !isNaN(parseFloat(row[column]))
                                                ? (
                                                    Math.round(
                                                      parseFloat(row[column]) *
                                                        100
                                                    ) / 100
                                                  ).toFixed(2)
                                                : row[column]}
                                            </td>
                                          ))}
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                                <Button
                                  className='download-btn'
                                  variant='contained'
                                  endIcon={<SimCardDownloadIcon />}
                                  onClick={() => {
                                    downloadCSV(message.id);
                                  }}
                                >
                                  Download CSV
                                </Button>
                              </div>
                            </div>
                          )}
                          {message.sql && (
                            <div className='collapse-container'>
                              <div className='collapse' id={`sql-${index}`}>
                                <pre>{message.sql}</pre>
                              </div>
                            </div>
                          )}
                          {/* {message.followup_questions && (
                          <div className="collapse-container">
                            <div className="collapse" id={`followup-${index}`}>
                              <ul>
                                {message.followup_questions.map((question, questionIndex) => (
                                  <li key={questionIndex} className="followup-question" onClick={() => sendQuestion(question)}>
                                    {question}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )} */}
                          {message.error && (
                            <p className='error-message'>
                              Error: {message.error}
                            </p>
                          )}
                        </div>
                        <div className='message-timestamp'>
                          {message.sender === "bot" && message.timestamp
                            ? `Processing time: ${
                                (message.timestamp -
                                  messages[index - 1].timestamp) /
                                1000
                              } seconds`
                            : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {loading && (
                  <div className='typing-indicator'>
                    <div className='typing-dot'></div>
                    <div className='typing-dot'></div>
                    <div className='typing-dot'></div>
                  </div>
                )}
              </div>
            </div>
            <div className='input-group mt-3'>
              <input
                type='text'
                className='form-control'
                // data-tooltip-id='exec-new-chat-tooltip'
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && newQuestion.trim() !== "") {
                    sendQuestion(newQuestion);
                  }
                }}
                placeholder='Query the data'
                // placeholder='Type your question...'
                aria-label='Question input'
              />
              {/* <button className="btn btn-custom" type="button" onClick={startRecording}>
              <i className="fas fa-microphone"></i>
            </button> */}
              {/* <input type="file" id="audioInput" accept="audio/*" onChange={onAudioFileChange} style={{ display: 'none' }} /> */}
              {!loading && (
                <button
                  className='btn btn-custom'
                  type='button'
                  onClick={handleReset}
                >
                  <i className='fas fa-rotate-left'></i>
                </button>
              )}
              <button
                className='btn btn-custom'
                type='button'
                onClick={() => {
                  if (newQuestion.trim() !== "") {
                    sendQuestion(newQuestion);
                  }
                }}
              >
                <i className='fas fa-paper-plane'></i>
              </button>
            </div>
          </div>
          <div className='debug-only'>
            <div className='col-md-4'>
              <div className='accordion' id='side-panel'>
                <div className='accordion-item'>
                  <h2 className='accordion-header' id='question-history-header'>
                    <button
                      className='accordion-button'
                      type='button'
                      data-bs-toggle='collapse'
                      data-bs-target='#question-history-content'
                      aria-expanded='true'
                      aria-controls='question-history-content'
                    >
                      Question History
                    </button>
                  </h2>
                  <div
                    id='question-history-content'
                    className='accordion-collapse collapse show'
                    aria-labelledby='question-history-header'
                  >
                    {questionHistory.length === 0 ? (
                      <div className='accordion-body empty-state'>
                        <i className='fas fa-history'></i>
                        <p>No questions asked yet.</p>
                      </div>
                    ) : (
                      <div className='accordion-body'>
                        <ul className='list-group'>
                          {questionHistory.map(
                            (
                              question: any,
                              index: number // Add type annotations for 'question' and 'index'
                            ) => (
                              <li
                                key={index}
                                className='list-group-item'
                                onClick={() => loadQuestion(question.id)}
                              >
                                {question.question}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className='accordion-item'>
                  <h2 className='accordion-header' id='training-data-header'>
                    <button
                      className='accordion-button collapsed'
                      type='button'
                      data-bs-toggle='collapse'
                      data-bs-target='#training-data-content'
                      aria-expanded='false'
                      aria-controls='training-data-content'
                    >
                      Training Data
                    </button>
                  </h2>
                  <div
                    id='training-data-content'
                    className='accordion-collapse collapse'
                    aria-labelledby='training-data-header'
                  >
                    <div className='accordion-body'>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          addTrainingData();
                        }}
                      >
                        <div className='mb-3'>
                          <label
                            htmlFor='training-data-type'
                            className='form-label'
                          >
                            Data Type:
                          </label>
                          <select
                            id='training-data-type'
                            className='form-select'
                            value={newTrainingData.training_data_type}
                            onChange={(e) =>
                              setNewTrainingData({
                                ...newTrainingData,
                                training_data_type: e.target.value,
                              })
                            }
                          >
                            <option value='question'>Question</option>
                            <option value='sql'>SQL</option>
                            <option value='ddl'>DDL</option>
                            <option value='documentation'>Documentation</option>
                          </select>
                        </div>
                        <div className='mb-3'>
                          <label
                            htmlFor='question-input'
                            className='form-label'
                          >
                            Question:
                          </label>
                          <input
                            type='text'
                            className='form-control'
                            id='question-input'
                            value={newTrainingData.question}
                            onChange={(e) =>
                              setNewTrainingData({
                                ...newTrainingData,
                                question: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className='mb-3'>
                          <label htmlFor='content-input' className='form-label'>
                            Content:
                          </label>
                          <textarea
                            className='form-control'
                            id='content-input'
                            rows={3}
                            value={newTrainingData.content}
                            onChange={(e) =>
                              setNewTrainingData({
                                ...newTrainingData,
                                content: e.target.value,
                              })
                            }
                          ></textarea>
                        </div>
                        <button type='submit' className='btn btn-custom'>
                          Add
                        </button>
                      </form>
                      <hr />
                      {trainingData.length === 0 ? (
                        <div className='empty-state'>
                          <i className='fas fa-database'></i>
                          <p>No training data yet.</p>
                        </div>
                      ) : (
                        <table className='table table-striped'>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Question</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trainingData.map((example) => (
                              <tr key={example.id}>
                                <td>
                                  <i
                                    className={`fas ${
                                      example.training_data_type === "question"
                                        ? "fa-question-circle"
                                        : example.training_data_type === "sql"
                                        ? "fa-database"
                                        : example.training_data_type === "ddl"
                                        ? "fa-table"
                                        : "fa-book"
                                    }`}
                                  ></i>
                                </td>
                                <td
                                  onClick={() =>
                                    showTrainingExampleContent(example)
                                  }
                                >
                                  {example.question}
                                </td>
                                <td>
                                  <button
                                    className='btn btn-link text-danger'
                                    onClick={() =>
                                      removeTrainingData(example.id)
                                    }
                                  >
                                    <i className='fas fa-trash'></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {selectedTrainingExample && (
              <div
                className='modal modal-sheet d-block'
                tabIndex={-1}
                role='dialog'
                id='training-example-modal'
              >
                <div className='modal-dialog' role='document'>
                  <div className='modal-content rounded-4 shadow'>
                    <div className='modal-header border-bottom-0'>
                      <h5 className='modal-title'>Training Example Content</h5>
                      <button
                        type='button'
                        className='btn-close'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                        onClick={() => setSelectedTrainingExample(null)}
                      ></button>
                    </div>
                    <div className='modal-body py-0'>
                      <pre>{selectedTrainingExample.content}</pre>
                    </div>
                    <div className='modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0'>
                      <button
                        type='button'
                        className='btn btn-lg btn-secondary'
                        data-bs-dismiss='modal'
                        onClick={() => setSelectedTrainingExample(null)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showAddTrainingDataModal && (
              <div
                className='modal modal-sheet d-block debug-only'
                tabIndex={-1}
                role='dialog'
                id='add-training-data-modal'
              >
                <div className='modal-dialog' role='document'>
                  <div className='modal-content rounded-4 shadow'>
                    <div className='modal-header border-bottom-0'>
                      <h5 className='modal-title'>Add Training Data</h5>
                      <button
                        type='button'
                        className='btn-close'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                        onClick={() => setShowAddTrainingDataModal(false)}
                      ></button>
                    </div>
                    <div className='modal-body py-0'>
                      <p>Do you want to add this query as training data?</p>
                    </div>
                    <div className='modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0'>
                      <button
                        type='button'
                        className='btn btn-lg btn-primary'
                        onClick={handleAddTrainingData}
                      >
                        Yes, add as training data
                      </button>
                      <button
                        type='button'
                        className='btn btn-lg btn-secondary'
                        data-bs-dismiss='modal'
                        onClick={() => setShowAddTrainingDataModal(false)}
                      >
                        No, don't add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <ReactTooltip
        id='exec-new-chat-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
          }),
        }}
        place='bottom'
        content='Query the data'
      /> */}
    </div>
  );
}

export default NewChatInterface;
